import React from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import {
  makeStyles,
  Box,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  List,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { stringConstants } from '../../constants/stringConstants';
import DisplayBreadcrumbs from '../../components/displayBreadcrumbs/displayBreadcrumbs';
import EditIcon from '@material-ui/icons/Edit';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import DraggableList from '../../components/draggable/draggableList';
import AlertDialog from '../../common/AlertDialog';
import DomainModal from './components/DomainModal';
import { PRODUCTS, useCommonAuth } from '../../submodule';

const useStyles = makeStyles((theme) => ({
  brandingboxWrapper: {
    width: '700px',
    margin: '0px auto',
  },
  logoImage: {
    width: '100%',
  },
  colorPicker: {
    marginTop: '7px',
  },
}));


function Domains() {
  const classes = useStyles();
  const { hasSettingsMenuAccess } = useCommonAuth();
  const history = useHistory();
  const [showModal, setShowModal] = React.useState(false);
  const [changeIndex, setChangeIndex] = React.useState(null);
  const [configData, userRole] = useStoreState((state) => [state.customize.configData, state.login.userInfo]);
  const customizeAction = useStoreActions((actions) => actions.customize);
  const settingsAction = useStoreActions((actions) => actions.settings);
  const commonAction = useStoreActions((action) => action.common);  
  const [domainsList, setDomainsList] = React.useState([]);
  const [deleteAlert, setDeleteAlert] = React.useState(false);
  const [defaultAlert, setDefaultAlert] = React.useState(false);

  const deleteAlertDialogOpts = {
    title: `Delete Domain - ${domainsList[changeIndex] ? domainsList[changeIndex].domain : ''}`,
    description: "Do you really want to delete this domain name?",
    btnOkText: "Yes",
    btnCancelText: "Cancel",
    onClose: (selectedValue) => {
      
      if (selectedValue) {
        // Delete the domain

        const domains = domainsList.map((item, index) => {
          if (index==changeIndex) item.active=false;
          return item;
        });

        updateAllowedDomains(domains);

        setDeleteAlert(false);
      }
      else {
        setDeleteAlert(false);
      }

      setChangeIndex(null);
    },
  };

  const defaultAlertDialogOpts = {
    title: `Set default domain - ${domainsList[changeIndex] ? domainsList[changeIndex].domain : ''}`,
    description: "Do you really want to set this domain as default?",
    btnOkText: "Yes",
    btnCancelText: "Cancel",
    onClose: (selectedValue) => {

      if (selectedValue) {
        const domains = domainsList.map((item, i) => {
          item.isDefault = (i==changeIndex);
          return item;
        });

        updateAllowedDomains(domains);

        setDefaultAlert(false);
      }
      else {
        setDefaultAlert(false);
      }

      setChangeIndex(null);

    },
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = ({ destination, source }) => {
    // dropped outside the list
    if (!destination) return;
    const newItems = reorder(domainsList, source.index, destination.index);

    //console.log('newItems=', newItems)

    const orderedItems = newItems.map( (item, index) => {
      return {...item, isDefault: index===0 }
    });

    //console.log('orderedItems=', orderedItems)

    setDomainsList(orderedItems);
    updateAllowedDomains(orderedItems);
  };

  const showDialog = (type) => {
    setShowModal(true);
  };

  const updateAllowedDomains = async (arr) => {
    try {
      await settingsAction.updateAllowedDomains({
        allowed_domains: arr,
      });
    
      commonAction.setSubmitSnackbar({
        status: true,
        messages: stringConstants.UPDATED,
      });
    }
    catch(error) {

      customizeAction.getConfigData();


      if (error.response && error.response.status && error.response.status==422 && error.response.data && error.response.data.error_type && error.response.data.error_type=="validation_error") {

        let errMsg = '';

        error.response.data.error.forEach( (item) => {
          if (Object.values(item)[0]) {
            errMsg = errMsg + Object.keys(item)[0] + ' - ' + Object.values(item)[0] + '\n';
          }
        });

        commonAction.setSubmitSnackbar({
          status: true,
          error: true,
          messages: errMsg,
        });
      }
    }
    
  };

  React.useEffect(() => {
    if (userRole.roles && !hasSettingsMenuAccess(PRODUCTS.MAINAPP, userRole?.roles?.sso_title)) {
      history.push('/');
    }
  }, [userRole?.roles]);

  React.useEffect(() => {
    if (configData?.allowed_domains) {      
      setDomainsList(configData.allowed_domains.sort( (a,b) => {
        if (!('active' in a)) a.active=true;
        if (!('active' in b)) b.active=true;
        return (a.active == b.active)? 0 : a.active ? -1 : 1;
      }));
    }
  }, [configData]);

  return (

    <Grid container direction="row">
      <Grid item md={12} xs={12}>            
        <DisplayBreadcrumbs items={[
          {
            title: "Domain Management",
            link: ""
          }
        ]} />
      </Grid>
      <Grid item md={12} style={{padding: '30px 20px 10px 20px'}}>
        <Box className={classes.brandingboxWrapper}>
          <Grid container direction="row">

            <Grid item container direction="column" md={10}>
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  Domain Management
                </Typography>
                <p>
                  You can manage the allowed domains here. You can drag a domain on the top of the list to set it as default.  
                </p>
              </Grid>
            </Grid>
            <Grid item md={2} align="right">
              <Tooltip title="Add Domain">
                <IconButton
                  color="primary"
                  component="span"
                  aria-label="Add"
                  onClick={() => showDialog('add')}
                >
                  <AddBoxIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item md={12}>
              <List>
                <DraggableList
                  items={domainsList.filter( i => i.active!==false)}
                  onDragEnd={onDragEnd}
                  labelKey={'domain'}
                  actionOnHover={true}
                  showSecondaryText={true}
                  actionItems={(value, index) => (
                    <>

                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => {
                          setChangeIndex(index);
                          setShowModal(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>

                      {!value.isDefault && (<Tooltip title="Delete"><IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => {
                          setChangeIndex(index);
                          setDeleteAlert(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton></Tooltip>)}
                    </>
                  )}
                />
              </List>
            </Grid>
          </Grid>

          <AlertDialog opts={defaultAlertDialogOpts}  open={defaultAlert}></AlertDialog>

          <AlertDialog opts={deleteAlertDialogOpts}  open={deleteAlert}></AlertDialog>

          <DomainModal
            open={showModal}
            opts={{
              title: domainsList[changeIndex]
                ? 'Edit Domain'
                : 'Add Domain',
              original: domainsList,
              changeIndex,
            }}
            onSave={() => {
              setChangeIndex(null);
              setShowModal(false);
            }}
            onCancel={() => {
              setChangeIndex(null);
              setShowModal(false);
            }}
          ></DomainModal>
        </Box>
      </Grid>    
    </Grid>
  );
}

export default Domains;
